import React from 'lib/react';
import {
  EVGrid, EVPaper, EVLink,
} from 'lib/components';
import Typography from 'commonComponents/typography';
import MembershipLevel from 'commonComponents/membershipLevel/MembershipLevel.component';
import { useTranslation } from 'lib/translation';
import { LEGACY_PORTAL_LINKS, ACCOUNT_LEVEL, CURRENCY_SYMBOL } from 'constants/AppConstants';
import PropTypes from 'prop-types';
import { getCurrencySymbol } from 'features/order/Order.utils';
import ManagePaymentOptionsStyles from '../ManagePaymentOptionsStyles';

function MembershipLevelSummary({ accountSummary, nextPlanLevelDetails }) {
  const classes = ManagePaymentOptionsStyles();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const currency = getCurrencySymbol(accountSummary?.currencyTypeId);

  if (accountSummary?.membershipLevel === ACCOUNT_LEVEL.CUSTOM) {
    return (
      <EVPaper className={classes.membershipLevelContainer}>
        <Typography variant="h3-600" fontColor="ocean-700">{t('dashboard__membership_Level')}</Typography>
        <EVGrid className={classes.customMembershipBox}>
          <EVGrid className={classes.customMembershipContent}>
            <Typography variant="h3-600" fontColor="charcoal-700">
              {t('manage_payment__current_level')}
            </Typography>
            <MembershipLevel level="Custom" />
            <EVLink className={classes.membershipLevelLink} href={LEGACY_PORTAL_LINKS.pricing} target="_blank">
              <Typography variant="h5-400" fontColor="ocean-700">{t('dashboard__pricing_and_membership_level')}</Typography>
            </EVLink>
          </EVGrid>
          <EVGrid className={classes.customMembershipContent}>
            <Typography variant="h4-700" fontColor="charcoal-700">{t('dashboard__year_to_date', { year: currentYear })}</Typography>
            <EVGrid className={classes.customMembershipSubContent}>
              <EVGrid style={{ width: '100%' }}>
                <Typography variant="subtitle-400" fontColor="charcoal-700">{t('manage_payment__reports_received')}</Typography>
                <Typography variant="h4-500" fontColor="charcoal-900">{accountSummary?.ytdReportsReceived}</Typography>
              </EVGrid>
              <EVGrid style={{ width: '100%' }}>
                <Typography variant="subtitle-400" fontColor="charcoal-700">{t('dashboard__prepayments')}</Typography>
                <Typography variant="h4-500" fontColor="charcoal-900">{`${currency} ${accountSummary.ytdPrepayments?.toFixed(2)}`}</Typography>
              </EVGrid>
            </EVGrid>
          </EVGrid>
        </EVGrid>
      </EVPaper>
    );
  }
  return (
    <EVPaper className={classes.membershipLevelContainer}>
      <Typography variant="h3-600" fontColor="ocean-700">{t('dashboard__membership_Level')}</Typography>
      <EVGrid className={classes.currentLevelBox}>
        <Typography variant="body-400" fontColor="charcoal-900">
          {t('manage_payment__current_level')}
        </Typography>
        <MembershipLevel level={accountSummary?.membershipLevel} />
        <EVLink className={classes.membershipLevelLink} href={LEGACY_PORTAL_LINKS.pricing} target="_blank">
          <Typography variant="h5-400" fontColor="ocean-600">{t('dashboard__pricing_and_membership_level')}</Typography>
        </EVLink>
      </EVGrid>
      <EVGrid className={classes.membershipLevelContentBox}>
        <Typography variant="body-400" fontColor="charcoal-900">
          {t('manage_payment__year_to_date')}
        </Typography>
        <EVGrid className={classes.membershipLevelContent}>
          <Typography variant="body-400" fontColor="charcoal-700">
            {t('manage_payment__received')}
          </Typography>
          <Typography variant="body-400" fontColor="charcoal-700" className={classes.value}>
            {accountSummary?.ytdReportsReceived}
            {' '}
            {accountSummary?.membershipLevel !== ACCOUNT_LEVEL.PLATINUM && t(
              'manage_payment__reports_to_reach_level',
              {
                minReportCount: (nextPlanLevelDetails?.minReportCount ?? 0) - (accountSummary?.ytdReportsReceived ?? 0),
                level: nextPlanLevelDetails?.nextMembershipLevel,
              },
            )}
          </Typography>
        </EVGrid>
      </EVGrid>
      <EVGrid className={classes.membershipLevelContentBox}>
        <Typography variant="body-400" fontColor="charcoal-900">
          {t('manage_payment__to_date')}
        </Typography>
        <Typography variant="h5-400" fontColor="charcoal-700">
          {t('manage_payment__include_monthly_payment')}
        </Typography>
        <EVGrid className={classes.membershipLevelContent}>
          <Typography variant="body-400" fontColor="charcoal-700">
            {t('manage_payment__deposited')}
          </Typography>
          <Typography variant="body-400" fontColor="charcoal-700" className={classes.value}>
            {CURRENCY_SYMBOL[1]}
            {accountSummary?.ytdPrepayments}
            {' '}
            {accountSummary?.membershipLevel !== ACCOUNT_LEVEL.PLATINUM && t(
              'manage_payment__deposit_to_reach_level',
              {
                minPrepaidCharge: (nextPlanLevelDetails?.minPrepaidCharge ?? 0) - (accountSummary?.ytdPrepayments ?? 0),
                level: nextPlanLevelDetails?.nextMembershipLevel,
              },
            )}
          </Typography>
        </EVGrid>
      </EVGrid>
      {accountSummary?.membershipLevel !== ACCOUNT_LEVEL.PLATINUM && (
      <EVGrid className={classes.upgradeBox}>
        <Typography variant="h4-600" fontColor="charcoal-900">
          {t('dashboard__upgrade_to_level', { upgradeToLevel: nextPlanLevelDetails?.nextMembershipLevel })}
        </Typography>
        <EVGrid className={classes.membershipLevelContent}>
          <Typography variant="h4-400" fontColor="charcoal-900">
            {t('manage_payment__set_monthly_prepayment')}
          </Typography>
          <Typography variant="h4-400" fontColor="charcoal-900" className={classes.value}>
            {CURRENCY_SYMBOL[1]}
            {nextPlanLevelDetails?.minMonthlyCharge}
          </Typography>
        </EVGrid>
        <Typography variant="label-600" fontColor="charcoal-900" style={{ display: ' flex', justifyContent: 'center' }}>
          {t('order__or')}
        </Typography>
        <EVGrid className={classes.membershipLevelContent}>
          <Typography variant="h4-400" fontColor="charcoal-900">
            {t('manage_payment__make_prepayment_deposit')}
          </Typography>
          <Typography variant="h4-400" fontColor="charcoal-900" className={classes.value}>
            {CURRENCY_SYMBOL[1]}
            {nextPlanLevelDetails?.minPrepaidCharge}
          </Typography>
        </EVGrid>
      </EVGrid>
      )}
    </EVPaper>
  );
}
MembershipLevelSummary.propTypes = {
  accountSummary: PropTypes.shape().isRequired,
  nextPlanLevelDetails: PropTypes.shape().isRequired,
};

export default MembershipLevelSummary;
